import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import './Onboarding.css';

const STEPS = ['Welcome', 'Departments', 'Locations', 'Done'];

export default function Onboarding() {
  const { org, updateOrg } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1 -- departments
  const [depts, setDepts] = useState([{ name: '', color: '#4f8ef7' }]);

  // Step 2 -- locations
  const [locations, setLocations] = useState([{ name: '', location_type: 'lane' }]);

  const COLORS = ['#4f8ef7','#22c55e','#f59e0b','#ef4444','#8b5cf6','#14b8a6','#f97316','#ec4899'];

  const addDept = () => setDepts([...depts, { name: '', color: '#4f8ef7' }]);
  const updateDept = (i, field, val) => {
    const d = [...depts];
    d[i][field] = val;
    setDepts(d);
  };
  const removeDept = (i) => setDepts(depts.filter((_, idx) => idx !== i));

  const addLoc = () => setLocations([...locations, { name: '', location_type: 'lane' }]);
  const updateLoc = (i, field, val) => {
    const l = [...locations];
    l[i][field] = val;
    setLocations(l);
  };
  const removeLoc = (i) => setLocations(locations.filter((_, idx) => idx !== i));

  const saveDepts = async () => {
    const valid = depts.filter(d => d.name.trim());
    if (valid.length === 0) { setStep(2); return; }
    setLoading(true); setError('');
    try {
      await Promise.all(valid.map(d => client.post('/departments', d)));
      setStep(2);
    } catch {
      setError('Failed to save departments. You can add them later in settings.');
      setTimeout(() => { setError(''); setStep(2); }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const saveLocs = async () => {
    const valid = locations.filter(l => l.name.trim());
    if (valid.length === 0) { finishOnboarding(); return; }
    setLoading(true); setError('');
    try {
      await Promise.all(valid.map(l => client.post('/locations', l)));
      finishOnboarding();
    } catch {
      setError('Failed to save locations. You can add them later in settings.');
      setTimeout(() => { setError(''); finishOnboarding(); }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = async () => {
    try {
      await client.put('/org/onboarded');
      // Update localStorage directly so ProtectedRoute sees it immediately
      const stored = localStorage.getItem('org');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.onboarded = true;
        localStorage.setItem('org', JSON.stringify(parsed));
      }
    } catch (err) {
      console.error(err);
    }
    setStep(3);
  };

  return (
    <div className="ob-page">
      <div className="ob-card">
        {/* Progress */}
        <div className="ob-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`ob-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <div className="ob-step-dot">{i < step ? '✓' : i + 1}</div>
              <div className="ob-step-label">{s}</div>
            </div>
          ))}
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="ob-body">
            <div className="ob-logo"><div className="ob-dot" /><span>TRACE</span></div>
            <h1 className="ob-title">Welcome, {org?.name}.</h1>
            <p className="ob-desc">Let's get your floor set up in about 2 minutes. We'll add your departments and locations so your team can start tracking jobs right away.</p>
            <button className="ob-btn" onClick={() => setStep(1)}>Let's Go →</button>
          </div>
        )}

        {/* Step 1: Departments */}
        {step === 1 && (
          <div className="ob-body">
            <h2 className="ob-title">Add your departments</h2>
            <p className="ob-desc">Departments help organize jobs on your floor. Examples: Press, Bindery, Finishing, Shipping.</p>
            {error && <div className="ob-error">{error}</div>}
            <div className="ob-list">
              {depts.map((d, i) => (
                <div key={i} className="ob-row">
                  <input
                    className="ob-input"
                    placeholder={`Department ${i + 1} (e.g. Press)`}
                    value={d.name}
                    onChange={e => updateDept(i, 'name', e.target.value)}
                  />
                  <div className="ob-colors">
                    {COLORS.map(c => (
                      <div
                        key={c}
                        className={`ob-color ${d.color === c ? 'selected' : ''}`}
                        style={{ background: c }}
                        onClick={() => updateDept(i, 'color', c)}
                      />
                    ))}
                  </div>
                  {depts.length > 1 && (
                    <button className="ob-remove" onClick={() => removeDept(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
            <button className="ob-add" onClick={addDept}>+ Add Department</button>
            <div className="ob-actions">
              <button className="ob-skip" onClick={() => setStep(2)}>Skip for now</button>
              <button className="ob-btn" disabled={loading} onClick={saveDepts}>
                {loading ? 'Saving...' : 'Next →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Locations */}
        {step === 2 && (
          <div className="ob-body">
            <h2 className="ob-title">Add your locations</h2>
            <p className="ob-desc">Locations are the physical spots on your floor -- lanes, machines, staging areas. Examples: Lane 1, Press 3, Staging A.</p>
            {error && <div className="ob-error">{error}</div>}
            <div className="ob-list">
              {locations.map((l, i) => (
                <div key={i} className="ob-row">
                  <input
                    className="ob-input"
                    placeholder={`Location ${i + 1} (e.g. Lane 1)`}
                    value={l.name}
                    onChange={e => updateLoc(i, 'name', e.target.value)}
                  />
                  <select
                    className="ob-select"
                    value={l.location_type}
                    onChange={e => updateLoc(i, 'location_type', e.target.value)}
                  >
                    <option value="lane">Lane</option>
                    <option value="machine">Machine</option>
                    <option value="staging">Staging</option>
                    <option value="other">Other</option>
                  </select>
                  {locations.length > 1 && (
                    <button className="ob-remove" onClick={() => removeLoc(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
            <button className="ob-add" onClick={addLoc}>+ Add Location</button>
            <div className="ob-actions">
              <button className="ob-skip" onClick={finishOnboarding}>Skip for now</button>
              <button className="ob-btn" disabled={loading} onClick={saveLocs}>
                {loading ? 'Saving...' : 'Finish Setup →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && (
          <div className="ob-body ob-done">
            <div className="ob-check">✓</div>
            <h2 className="ob-title">You're all set.</h2>
            <p className="ob-desc">Your workspace is ready. Start adding jobs to the queue and your team can check in from the kiosk.</p>
            <button className="ob-btn" onClick={() => window.location.href = '/dashboard'}>
              Go to Dashboard →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}